const React = require('react');
const T = require('prop-types');
const { useState, useEffect, useRef, useCallback } = React;
const { context } = require('app-context');
const { USER_ROLE_IDS } = require('utils/constants');
const useForm = require('hooks/useForm');
const FormConfig = require('./formConfig');

const { default: Box } = require('@mui/material/Box');
const { default: Typography } = require('@mui/material/Typography');
const { default: Button } = require('@mui/material/Button');
const { default: IconButton } = require('@mui/material/IconButton');
const { default: Checkbox } = require('@mui/material/Checkbox');
const { default: FormControlLabel } = require('@mui/material/FormControlLabel');
const { default: AddIcon } = require('@mui/icons-material/Add');
const { default: DeleteIcon } = require('@mui/icons-material/Delete');

const selectors = context.selectors.all;
const actions = context.actions;
const redux = context.redux.hooks;

const internals = {};

let rowKeySeq = 0;
const nextRowKey = () => `row-${++rowKeySeq}-${Date.now()}`;

const emptyRow = () => ({
    key: nextRowKey(),
    targetId: null,
    schoolId: null,
    roleIds: [],
    allProspectsSelected: false
});

const deriveInitialModel = (initialTargets) => {

    const targets = initialTargets || [];
    const schoolRoleTargets = targets.filter((t) => t.targetType === 'schoolRole');

    if (schoolRoleTargets.length > 0) {

        const hasAllSchools = schoolRoleTargets.some((t) => String(t.targetValue).split(':')[0] === 'all');

        if (hasAllSchools) {
            return {
                mode: 'allProspects',
                rows: [],
                allProspectsTargetId: schoolRoleTargets.find((t) => String(t.targetValue).split(':')[0] === 'all')?.id
            };
        }

        const rows = schoolRoleTargets.map((t) => {

            const [schoolPart, rolePart] = String(t.targetValue).split(':');

            return {
                key: nextRowKey(),
                targetId: t.id,
                schoolId: Number(schoolPart),
                roleIds: (rolePart || '').split(',').filter(Boolean).map(Number),
                allProspectsSelected: false
            };
        });

        return { mode: 'custom', rows, allProspectsTargetId: null };
    }

    return {
        mode: 'custom',
        rows: [emptyRow()],
        allProspectsTargetId: null
    };
};

const targetsAllProspects = (targets) => (targets || []).some((t) =>
    t.targetType === 'schoolRole' && String(t.targetValue).split(':')[0] === 'all');

const buildSchoolRoleTarget = ({ id, targetValue }) => {

    const target = {
        targetType: 'schoolRole',
        targetValue,
        combinationType: 'OR'
    };

    if (Number.isInteger(id)) {
        target.id = id;
    }

    return target;
};

const buildTargets = (mode, rows, allProspectsTargetId) => {

    if (mode === 'allProspects') {
        return [buildSchoolRoleTarget({
            id: allProspectsTargetId,
            targetValue: `all:${USER_ROLE_IDS.PROSPECT.join(',')}`
        })];
    }

    return rows
        .filter((row) => row.schoolId && row.roleIds.length > 0)
        .map((row) => buildSchoolRoleTarget({
            id: row.targetId,
            targetValue: `${row.schoolId}:${row.roleIds.join(',')}`
        }));
};

const computeIsValid = (mode, rows) => {

    if (mode === 'allProspects') {
        return true;
    }

    if (rows.length === 0) {
        return false;
    }

    return rows.every((row) => row.schoolId && row.roleIds.length > 0);
};

module.exports = function ResourceTargeting(props) {

    const { initialTargets, onChange, onValidityChange } = props;
    const { TargetingRow } = internals;

    const initialModelRef = useRef(null);
    if (initialModelRef.current === null) {
        initialModelRef.current = deriveInitialModel(initialTargets);
    }

    const [mode, setMode] = useState(initialModelRef.current.mode);
    const [rows, setRows] = useState(initialModelRef.current.rows);
    const [schools, setSchools] = useState(() => selectors.getSchools(redux.getState()) || []);
    const [schoolsLoading, setSchoolsLoading] = useState(false);
    const [rolesBySchool, setRolesBySchool] = useState({});
    const [loadingSchoolIds, setLoadingSchoolIds] = useState([]);

    const onChangeRef = useRef(onChange);
    useEffect(() => {

        onChangeRef.current = onChange;
    }, [onChange]);

    const onValidityChangeRef = useRef(onValidityChange);
    useEffect(() => {

        onValidityChangeRef.current = onValidityChange;
    }, [onValidityChange]);

    useEffect(() => {

        onChangeRef.current(buildTargets(mode, rows, initialModelRef.current.allProspectsTargetId));

        if (onValidityChangeRef.current) {
            onValidityChangeRef.current(computeIsValid(mode, rows));
        }
    }, [mode, rows]);

    const rolesBySchoolRef = useRef(rolesBySchool);
    useEffect(() => {

        rolesBySchoolRef.current = rolesBySchool;
    }, [rolesBySchool]);

    const loadRolesForSchool = useCallback((schoolId) => {

        if (!schoolId || rolesBySchoolRef.current[schoolId]) {
            return;
        }

        setLoadingSchoolIds((prev) => prev.includes(schoolId) ? prev : [...prev, schoolId]);

        redux.dispatch(actions.dataFetching.fetchAppEntities({ entities: ['schoolDetails'], schoolId }))
            .then(() => {

                const persistedRoleIds = initialModelRef.current.rows
                    .filter((row) => String(row.schoolId) === String(schoolId))
                    .flatMap((row) => row.roleIds);
                const prospectRoles = FormConfig.getProspectResourceRoles(
                    selectors.getSchoolRoles_byId(redux.getState(), schoolId) || [],
                    persistedRoleIds
                );

                setRolesBySchool((prev) => ({ ...prev, [schoolId]: prospectRoles }));
            })
            .finally(() => setLoadingSchoolIds((prev) => prev.filter((id) => id !== schoolId)));
    }, []);

    useEffect(() => {

        if (schools.length === 0) {
            setSchoolsLoading(true);
            redux.dispatch(actions.dataFetching.fetchAppEntities({ entities: ['schools'] }))
                .then(() => setSchools(selectors.getSchools(redux.getState()) || []))
                .finally(() => setSchoolsLoading(false));
        }

        initialModelRef.current.rows.forEach((row) => loadRolesForSchool(row.schoolId));
    }, [loadRolesForSchool]);

    const handleToggleAllProspects = useCallback((event) => {

        if (event.target.checked) {
            setMode('allProspects');
            return;
        }

        setMode('custom');
        setRows((prev) => prev.length > 0 ? prev : [emptyRow()]);
    }, []);

    const handleAddRow = useCallback(() => {

        setRows((prev) => [...prev, emptyRow()]);
    }, []);

    const handleRemoveRow = useCallback((key) => {

        setRows((prev) => prev.filter((row) => row.key !== key));
    }, []);

    const handleSchoolChange = useCallback((key, school) => {

        const schoolId = school ? school.id : null;
        setRows((prev) => prev.map((row) => row.key === key ? {
            ...row,
            schoolId,
            roleIds: [],
            allProspectsSelected: false
        } : row));

        if (schoolId) {
            loadRolesForSchool(schoolId);
        }
    }, [loadRolesForSchool]);

    const handleRolesChange = useCallback((key, schoolId, value) => {

        const allIds = (rolesBySchoolRef.current[schoolId] || []).map((role) => role.id);

        setRows((prev) => prev.map((row) => {

            if (row.key !== key) {
                return row;
            }

            return {
                ...row,
                ...FormConfig.resolveProspectRoleSelection({
                    allRoleIds: allIds,
                    previousAllProspectsSelected: row.allProspectsSelected,
                    value
                })
            };
        }));
    }, []);

    return (
        <Box>
            <h3 style={{ marginBottom: 0 }}>Targeting Rules</h3>
            <Typography variant='caption' color='text.secondary' sx={{ mb: 1, display: 'block' }}>
                Choose which schools and prospect roles can see this resource.
            </Typography>

            <FormControlLabel
                control={<Checkbox checked={mode === 'allProspects'} onChange={handleToggleAllProspects} />}
                label='Target all prospects across all schools'
            />

            {mode === 'custom' && (
                <Box sx={{ mt: 1 }}>
                    <Typography variant='caption' color='text.secondary' sx={{ mb: 1, display: 'block' }}>
                        Add a row per school and pick which prospect roles can see this resource.
                    </Typography>

                    {rows.map((row) => {

                        const available = rolesBySchool[row.schoolId] || [];
                        const isLoading = loadingSchoolIds.includes(row.schoolId);

                        return (
                            <TargetingRow
                                key={row.key}
                                availableRoles={available}
                                allProspectsSelected={row.allProspectsSelected}
                                isLoading={isLoading}
                                onRemove={() => handleRemoveRow(row.key)}
                                onRolesChange={(value) => handleRolesChange(row.key, row.schoolId, value)}
                                onSchoolChange={(schoolId) => handleSchoolChange(
                                    row.key,
                                    schools.find((school) => String(school.id) === String(schoolId)) || null
                                )}
                                roleIds={row.roleIds}
                                rowKey={row.key}
                                schoolId={row.schoolId}
                                schools={schools}
                                schoolsLoading={schoolsLoading}
                            />
                        );
                    })}

                    {!computeIsValid(mode, rows) && (
                        <Typography variant='caption' color='error' sx={{ mt: 1, display: 'block' }}>
                            Each row needs a school and at least one prospect role.
                        </Typography>
                    )}

                    <Button
                        startIcon={<AddIcon />}
                        onClick={handleAddRow}
                        size='small'
                        sx={{ mt: rows.length > 0 ? 1 : 0 }}
                    >
                        Add School
                    </Button>
                </Box>
            )}
        </Box>
    );
};

internals.TargetingRow = function TargetingRow(props) {

    const {
        onRemove,
        ...formConfigProps
    } = props;
    const { Form } = useForm(FormConfig.buildTargetingRowFormConfig(formConfigProps));

    return (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                {Form}
            </Box>
            <IconButton size='small' onClick={onRemove} color='error' aria-label='Remove row'>
                <DeleteIcon fontSize='small' />
            </IconButton>
        </Box>
    );
};

module.exports.targetsAllProspects = targetsAllProspects;
module.exports.deriveInitialModel = deriveInitialModel;
module.exports.buildTargets = buildTargets;
module.exports.computeIsValid = computeIsValid;

module.exports.propTypes = {
    initialTargets: T.array,
    onChange: T.func.isRequired,
    onValidityChange: T.func
};
